import { useTranslation } from "react-i18next";
import { cx } from "./kit";

export const REGIONS = ["head", "ears", "neck", "chest", "back", "belly", "front_legs", "hind_legs", "paws", "tail"];
export const SIDES = ["left", "right", "both", "centre"];

// Top-down schematic: left side of dog = left half of the drawing.
const SHAPES = {
  head: { el: "ellipse", p: { cx: 160, cy: 52, rx: 30, ry: 26 } },
  ears: { el: "group", parts: [{ cx: 124, cy: 40, rx: 12, ry: 18 }, { cx: 196, cy: 40, rx: 12, ry: 18 }] },
  neck: { el: "rect", p: { x: 138, y: 78, width: 44, height: 26, rx: 10 } },
  chest: { el: "rect", p: { x: 120, y: 104, width: 80, height: 40, rx: 18 } },
  back: { el: "rect", p: { x: 116, y: 144, width: 88, height: 96, rx: 22 } },
  belly: { el: "rect", p: { x: 130, y: 240, width: 60, height: 34, rx: 14 } },
  front_legs: { el: "group", rects: [{ x: 88, y: 110, width: 30, height: 56, rx: 12 }, { x: 202, y: 110, width: 30, height: 56, rx: 12 }] },
  hind_legs: { el: "group", rects: [{ x: 88, y: 230, width: 30, height: 58, rx: 12 }, { x: 202, y: 230, width: 30, height: 58, rx: 12 }] },
  paws: { el: "group", parts: [{ cx: 103, cy: 176, rx: 13, ry: 9 }, { cx: 217, cy: 176, rx: 13, ry: 9 }, { cx: 103, cy: 298, rx: 13, ry: 9 }, { cx: 217, cy: 298, rx: 13, ry: 9 }] },
  tail: { el: "rect", p: { x: 152, y: 276, width: 16, height: 44, rx: 8 } },
};

export default function BodyMap({ value, onSelect, counts = {}, testid = "dog-record-body-map" }) {
  const { t } = useTranslation();
  const fill = (r) => (value === r ? "hsl(14 56% 40%)" : counts[r] ? "hsl(268 45% 82%)" : "hsl(35 30% 92%)");
  const stroke = (r) => (value === r ? "hsl(14 56% 28%)" : "hsl(24 12% 50%)");
  const common = (r) => ({
    role: "button", tabIndex: 0, "aria-label": `${t(`region.${r}`)}${counts[r] ? ` — ${t("bodymap.count", { count: counts[r] })}` : ""}`, "aria-pressed": value === r,
    "data-testid": `body-region-${r}`, onClick: () => onSelect?.(r), onKeyDown: (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect?.(r)),
    style: { cursor: "pointer", transition: "fill .15s" }, fill: fill(r), stroke: stroke(r), strokeWidth: 2,
  });
  return (
    <div data-testid={testid} className="rounded-2xl border bg-white p-3">
      <div className="mb-1 flex justify-between px-2 text-xs font-semibold text-muted-foreground"><span>{t("side.left")}</span><span>{t("bodymap.top_view")}</span><span>{t("side.right")}</span></div>
      <svg viewBox="70 10 180 320" className="mx-auto h-[300px] w-full max-w-[260px]" aria-label={t("bodymap.title")}>
        {REGIONS.map((r) => {
          const s = SHAPES[r];
          if (s.el === "ellipse") return <ellipse key={r} {...s.p} {...common(r)} />;
          if (s.el === "rect") return <rect key={r} {...s.p} {...common(r)} />;
          return (
            <g key={r} {...common(r)}>
              {(s.parts || []).map((p, i) => <ellipse key={i} {...p} />)}
              {(s.rects || []).map((p, i) => <rect key={i} {...p} />)}
            </g>
          );
        })}
      </svg>
      <p className="px-2 text-center text-xs text-muted-foreground">{t("bodymap.hint")}</p>
    </div>
  );
}

export function RegionList({ value, onSelect, counts = {} }) {
  const { t } = useTranslation();
  return (
    <ul className="grid grid-cols-2 gap-1.5" aria-label={t("bodymap.text_list")} data-testid="body-region-text-list">
      {REGIONS.map((r) => (
        <li key={r}>
          <button data-testid={`region-list-${r}`} onClick={() => onSelect(r)} aria-pressed={value === r}
            className={cx("flex min-h-[44px] w-full items-center justify-between rounded-xl border px-3 text-left text-sm font-semibold", value === r ? "border-primary bg-[hsl(var(--peach))]" : "bg-white hover:bg-muted")}>
            {t(`region.${r}`)} {counts[r] ? <span className="text-xs text-muted-foreground">{t("bodymap.count", { count: counts[r] })}</span> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}
